import React, { useContext, useEffect, useState } from 'react';
import { Modal, Tooltip } from 'antd';
import Upload from 'antd/lib/upload';
import { Button, FormContext, FormItem, FormSubmitButton, MasterDetailForm, notification, Table } from '@digital/ui';
import api from '@app/api/api';
import {
  ArquivoContratoAditivo,
  DeleteFileComentarioLinhaBmComAnexo,
  GetAllComentarioLinhaBmComAnexo,
  GetFileComentarioLinhaBmComAnexo,
  ViewFileComentarioLinhaBmComAnexo,
} from '../../../api/contrato/aditivosContrato';
import { GetFileExtension, getLoggedIsMRN, imageFormats } from '@app/helpers';
import { hasPermission } from '../../../helpers/permissions';
import { convertUTCtoLocal } from '@app/helpers/dateTimeFormats';
import moment from 'moment';
import { DownloadAnexosLinhaMedicao } from '@app/api/boletimMedicao';

export interface PropsModalAnexo {
  linhaBmId: string;
  contemAnexo: boolean;
  usuarioLogadoGestorDoContratoFornecedor: boolean;
  usuarioLogadoFiscalDoContratoFornecedor: boolean;
  codStatusMedicao: number;
  onAnexosAlterados: () => void;
}

interface PropsUploadAnexosLinha {
  linhaBmId: string;
  onFilesChange: (files: File[]) => void;
}

const UploadAnexosLinha = ({ linhaBmId, onFilesChange }: PropsUploadAnexosLinha) => {
  const [arquivos, setArquivos] = useState<any[]>([]);
  const { formProps } = useContext(FormContext);

  useEffect(() => {
    const files = arquivos.map(arquivo => arquivo.originFileObj);
    formProps && formProps.setFieldValue('arquivos', files);
    onFilesChange(files);
  }, [arquivos]);

  const columns = [
    {
      title: 'Nome',
      dataIndex: 'name',
    },
    {
      title: 'Ações',
      width: '10%',
      render: (_text, record) => (
        <Button
          icon="commons-trash"
          onClick={() => setArquivos(arquivosAtuais => arquivosAtuais.filter(arquivo => arquivo.uid !== record.uid))}
        />
      ),
    },
  ];

  return (
    <FormItem name="arquivos" span={24}>
      <div className="upload-anexos-linha-container">
        <div className="upload-anexos-linha-cabecalho">
          <div className="upload-anexos-linha-descricao">
            <strong>Novos arquivos</strong>
            <span>Selecione um ou mais arquivos para enviar.</span>
          </div>
          <Upload
            id={`anexoLinhaBm_${linhaBmId}`}
            multiple
            accept="*/*"
            fileList={arquivos}
            showUploadList={false}
            beforeUpload={() => false}
            onChange={event => setArquivos(event.fileList)}>
            <Button type="primary" icon="ui-upload">
              Adicionar arquivos
            </Button>
          </Upload>
        </div>
        {arquivos.length > 0 ? (
          <Table
            className="upload-multiple-table"
            pagination={false}
            rowKey="uid"
            dataSource={arquivos}
            columns={columns}
          />
        ) : null}
      </div>
    </FormItem>
  );
};

const ModalAnexo = (props: PropsModalAnexo) => {
  const {
    linhaBmId,
    contemAnexo,
    codStatusMedicao,
    usuarioLogadoFiscalDoContratoFornecedor,
    usuarioLogadoGestorDoContratoFornecedor,
    onAnexosAlterados,
  } = props;
  const [exibirModal, setExibirModal] = useState(false);
  const [dadosAnexo, setDadosAnexo] = useState<ArquivoContratoAditivo[]>([]);
  const [iconeAnexo, setIconeAnexo] = useState('');
  const [isMRN] = useState<boolean>(getLoggedIsMRN());
  const [uploadFormKey, setUploadFormKey] = useState(0);
  const [enviando, setEnviando] = useState(false);
  const [quantidadeArquivosSelecionados, setQuantidadeArquivosSelecionados] = useState(0);

  const podeAdicionarAnexo =
    (isMRN ||
      ((codStatusMedicao === 1 || codStatusMedicao === 3) &&
        (usuarioLogadoFiscalDoContratoFornecedor === true ||
          usuarioLogadoGestorDoContratoFornecedor === true))) &&
    hasPermission(['MEDICAO_QUANTITATIVA_CADASTRAR_ANEXO']);

  useEffect(() => {
    if (contemAnexo === true) {
      MudarIconeAnexo();
    }
  }, []);

  const MudarIconeAnexo = () => {
    setIconeAnexo('has-attachments');
  };

  const getAll = async () => {
    const result = await GetAllComentarioLinhaBmComAnexo(linhaBmId);
    if (result) {
      setDadosAnexo(result);
      setIconeAnexo(result.length > 0 ? 'has-attachments' : '');
    }
  };

  const CarregarDados = () => {
    getAll();
  };

  const ObterArquivo = (id: string, nome: string) => {
    GetFileComentarioLinhaBmComAnexo(id, nome);
  };

  const ViewArquivo = (id: string, nome: string) => {
    ViewFileComentarioLinhaBmComAnexo(id, nome);
  };

  const DeleteAnexo = (id: string) => {
    DeleteFileComentarioLinhaBmComAnexo(id).then(result => {
      CarregarDados();
      onAnexosAlterados();
    });
  };

  const EnviarArquivos = async (values: any) => {
    const arquivos = (values.arquivos || []) as File[];
    if (arquivos.length === 0) {
      notification.error('Selecione pelo menos um arquivo.');
      return;
    }

    setEnviando(true);
    let ocorreuErro = false;
    let arquivoEnviado = false;

    try {
      for (const arquivo of arquivos) {
        const form = new FormData();
        form.append('File', arquivo);
        form.append('anexoArquivoLinhaMedicaoId', linhaBmId);

        const data = await api.post('medicao/anexoArquivoLinhaMedicao', form, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          disableNotification: true,
        });

        if (!data || data.invalid || data.valid === false) {
          ocorreuErro = true;
          if (data && data.notifications) {
            data.notifications.forEach(notificacao => notification.error(notificacao.message));
          }
          break;
        }

        arquivoEnviado = true;
      }

      if (arquivoEnviado) {
        CarregarDados();
        MudarIconeAnexo();
        onAnexosAlterados();
      }
      if (!ocorreuErro) {
        notification.success('Arquivos adicionados com sucesso.');
      }

      setUploadFormKey(valor => valor + 1);
      setQuantidadeArquivosSelecionados(0);
    } finally {
      setEnviando(false);
    }
  };

  const columns = [
    {
      title: 'Numero',
      dataIndex: 'numero',
      width: '10%',
    },
    {
      title: 'Nome',
      dataIndex: 'nome',
      width: '20%',
    },
    {
      title: 'Inclusão',
      dataIndex: 'createdAt',
      width: '20%',
      render : (text: any) => {
        return convertUTCtoLocal(moment(text)).toLocaleString();
      }
    },
    {
      title: 'Responsável',
      dataIndex: 'responsavel',
      width: '20%',
    },
    {
      title: 'Ações',
      dataIndex: '',
      key: 'x',
      width: '5%',
      render: record => (
        <>
          {
            <>
              <Tooltip title="Baixar Arquivo">
                <Button
                  onClick={() => {
                    ObterArquivo(record.id, record.nome);
                  }}
                  icon="ui-download"
                  name="btnBaixarArquivo"
                />
              </Tooltip>
              {GetFileExtension(record.nome) === 'pdf' || imageFormats.includes(GetFileExtension(record.nome)) ? (
                <Tooltip title="Visualizar Arquivo">
                  <Button
                    onClick={async () => {
                      await ViewArquivo(record.id, record.nome);
                    }}
                    icon="files-view_file"
                    name="btnViewArquivo"
                  />
                </Tooltip>
              ) : (
                <></>
              )}
              {(isMRN ||
                ((codStatusMedicao === 1 || codStatusMedicao === 3) && // codigo 1 = Aguardando Forncedor    codigo 3 = Aguardando correção do fornecedor
                  (usuarioLogadoFiscalDoContratoFornecedor === true ||
                    usuarioLogadoGestorDoContratoFornecedor === true))) &&
              hasPermission(['MEDICAO_QUANTITATIVA_CADASTRAR_ANEXO']) ? (
                <Tooltip title="Excluir Anexo">
                  <Button
                    onClick={async () => {
                      await DeleteAnexo(record.id);
                    }}
                    icon="commons-delete"
                    name="btnDeleteArquivo"
                    confirm
                    okText="Excluir"
                  />
                </Tooltip>
              ) : (
                <></>
              )}
            </>
          }
        </>
      ),
    },
  ];

  return (
    <>
      <Tooltip title="Anexos">
        <Button
          onClick={() => {
            setExibirModal(true);
            CarregarDados();
          }}
          icon="messaging-attach"
          className={iconeAnexo}
        />
      </Tooltip>
      <Modal
        title="Anexos"
        className="chat-modal"
        width="800px"
        visible={exibirModal}
        onCancel={() => {
          setExibirModal(false);
        }}
        footer={null}>
        <MasterDetailForm
          key={`form-anexos-${linhaBmId}-${uploadFormKey}`}
          initialValues={{ arquivos: [] }}
          onSubmit={EnviarArquivos}
          actions={[
            <Button key="close" type="link" onClick={() => setExibirModal(false)}>
              Fechar
            </Button>,
            dadosAnexo.length > 0 ? (
              <Button
                key="download"
                type="primary"
                icon="ui-download"
                onClick={() => DownloadAnexosLinhaMedicao(linhaBmId)}>
                Baixar anexos
              </Button>
            ) : null,
            podeAdicionarAnexo && quantidadeArquivosSelecionados > 0 ? (
              <FormSubmitButton
                key="upload"
                noIcon
                type="primary"
                className="enviar-anexos-linha"
                disabled={enviando}>
                Enviar arquivos
              </FormSubmitButton>
            ) : null,
          ]}>
          <div className="all-padding">
            <Table dataSource={dadosAnexo} columns={columns} rowKey="id" pagination={false} />
            {podeAdicionarAnexo ? (
              <UploadAnexosLinha
                linhaBmId={linhaBmId}
                onFilesChange={arquivos => setQuantidadeArquivosSelecionados(arquivos.length)}
              />
            ) : null}
          </div>
        </MasterDetailForm>
      </Modal>
    </>
  );
};

export default ModalAnexo;
